import {
  CameraOutlined,
  EyeOutlined,
  PaperClipOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import React, { useState } from 'react';
import { Upload, Button, message, Modal, Spin, Tooltip } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import firebase from '../../../firebase';
import { getContentTypeIconMap } from 'Views/Reports/Report';
import { RcFile, UploadFile } from 'antd/lib/upload/interface';
import confirmModal from '../../../general/confirmModal';
import useGetDownloadUrl from 'hooks/useGetDownloadUrl';
import styled from 'styled-components';
import { useAppSelector } from '../../../hooks';
import { StyleGuide } from 'styles/StyleGuide';
import { ContentType } from 'general/enums';
import { getExtentionByMimeType } from 'general/files';
import { FileSpreadsheet } from 'lucide-react';
import { getFileIcon } from '@/lib/file-icons';

interface Props {
  orgId: string;
  contextId: string;
  context: string;
  subContextId?: string; // this is the taskId
  deleteFile: (fileId: string) => void;
  disabled?: boolean;
  showRequiredIndicator?: boolean;
}

export default function AuditTaskImages(props: Props) {
  const files = useAppSelector((state) =>
    state.files.files
      .map((file: any) => ({
        uid: file.id,
        filePath: file.filePath || file.fileId,
        thumbFilePath: file.thumbFilePath || file.fileId,
        name: file?.fileName || 'xxx.png',
        status: 'done',
        url: file.thumbnail || file.path,
        taskId: file.subContextId || file.taskId,
        contentType: file.contentType,
      }))

      .filter((file) => file.taskId === props.subContextId)
  );
  return (
    <>
      <PicturesWall
        disabled={props.disabled}
        files={files}
        orgId={props.orgId}
        contextId={props.contextId}
        subContextId={props.subContextId}
        context="AUDIT_DOCUMENTATION"
        deleteFile={props.deleteFile}
        showRequiredIndicator={props.showRequiredIndicator}
      />
    </>
  );
}

interface PWProps {
  files: any[];
  orgId: string;
  contextId: string;
  context: string;
  subContextId?: string;
  deleteFile: (fileId: string) => void;
  disabled?: boolean;
  showRequiredIndicator?: boolean;
}
type Preview = {
  fileId: string | null;
  contentType: ContentType | null;
  previewVisible: boolean;
  previewImage?: string;
  filePath?: string; // Added for download
  fileName?: string; // Added for download
};
function PicturesWall(props: PWProps) {
  console.log({ props });
  const [preview, setPreview] = useState<Preview>({
    previewVisible: false,
    previewImage: '',
    fileId: null,
    contentType: null,
  });
  const [uploading, setUploading] = useState(false);

  const handleCancel = () => setPreview({ ...preview, previewVisible: false });

  const handlePreview = (file: UploadFile<any>) => {
    setPreview({
      fileId: file.uid,
      previewImage: file.url || file.thumbUrl,
      previewVisible: true,
      // @ts-ignore
      contentType: file.contentType,
    });
  };

  const beforeUpload = (file: RcFile) => {
    setUploading(true);
    firebase
      .storage()
      .ref('orgs/' + props.orgId + '/' + props.context)
      // @ts-ignore
      .putFiles([file], props.orgId, props.contextId, props.context, props.subContextId)
      .then(function (metadatas: any[]) {
        // Get an array of file metadata
        const stats = {
          files: metadatas.map((file) => file.metadata.name),
        };

        // firebase.firestore().collection(`orgs/${'test'}/issues`).doc('issueId').set(stats)
        // self.setState({
        //     uploading: false,
        // });
        message.success('Fil uploadet');
        return true;
      })
      .catch(function (error: any) {
        console.log(error);
        // self.setState({
        //     uploading: false,
        // });
        return false;
      })
      .finally(function () {
        setUploading(false);
      });
  };

  const { previewVisible, previewImage, filePath, fileName } = preview;
  console.log('PicturesWall - Preview filePath for modal title/image view:', filePath);
  // State for the download button itself
  const [downloadButtonLoading, setDownloadButtonLoading] = useState(false);
  // We will fetch the fileUrl for download on demand inside handleDownloadClick

  const handleDownloadClick = async () => {
    // User specified preview.previewImage as the path for download
    const storagePathForDownload = preview.previewImage;

    if (!storagePathForDownload) {
      console.error(
        'Download clicked but no preview.previewImage (storage path) available in preview state.'
      );
      message.error('Kan ikke downloade fil: sti til fil mangler.');
      return;
    }
    if (!fileName) {
      console.error('Download clicked but no fileName available in preview state.');
      // We can still try to download, but maybe with a generic name or no name suggestion
    }

    setDownloadButtonLoading(true);
    try {
      // Using storagePathForDownload (derived from preview.previewImage) as per user instruction
      const url = await firebase.storage().ref(storagePathForDownload).getDownloadURL();
      let finalFileName = preview.fileName || 'download';
      const lastDotIndex = finalFileName.lastIndexOf('.');
      const hasExtension = lastDotIndex > 0 && lastDotIndex < finalFileName.length - 1;

      if (!hasExtension && preview.contentType) {
        const extension = getExtentionByMimeType(preview.contentType);

        if (extension) {
          finalFileName = `${finalFileName}.${extension}`;
        }
      }

      // Fetch the file as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();

      // Create an object URL for the blob
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = finalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the object URL to free up resources
      URL.revokeObjectURL(blobUrl);
      message.success('Fil download startet.');
    } catch (error) {
      console.error('Error fetching download URL for download button:', error);
      message.error('Fejl ved download af fil.');
    }
    setDownloadButtonLoading(false);
  };

  const confirmDelete = (fileId: string) => {
    confirmModal({
      title: 'Er du sikker på du vil slette billedet?',
      onOk: () => {
        props.deleteFile(fileId);
        setPreview({
          fileId: null,
          previewVisible: false,
          previewImage: undefined,
          contentType: null,
        });
      },
    });
  };
  return (
    <div className="custom-audit-file-upload">
      <Upload
        fileList={props.files}
        onPreview={handlePreview}
        beforeUpload={beforeUpload}
        onRemove={(file) => confirmDelete(file.uid)}
        itemRender={(_, file: any) => {
          console.log({ fileType: file.contentType });
          const icon = getContentTypeIconMap(file?.contentType, file?.thumbFilePath, {
            height: 30,
            width: 30,
            borderRadius: 4,
            // marginTop: 20,
            fontSize: 55,
          });

          return (
            <Tooltip title="Se fil">
              <div>
                <ViewFile
                  icon={icon}
                  filePath={file.filePath}
                  openPreview={() =>
                    setPreview({
                      fileId: file.uid,
                      previewVisible: true,
                      previewImage: file.filePath || file.thumbFilePath,
                      contentType: file.contentType,
                    })
                  }
                />
              </div>
            </Tooltip>
          );
        }}
      >
        <Tooltip title="Tilføj filer">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Button
              loading={uploading}
              disabled={props.disabled}
              icon={<PaperClipOutlined />}
            ></Button>
            {props.showRequiredIndicator && (
              <div
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 8,
                  height: 8,
                  backgroundColor: StyleGuide.palette.redish,
                  borderRadius: '50%',
                }}
              />
            )}
          </div>
        </Tooltip>
      </Upload>
      <Modal
        open={previewVisible}
        title={fileName || 'Forhåndsvisning'}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadButtonLoading}
            disabled={!preview.previewImage || downloadButtonLoading} // Disabled if no previewImage path
            onClick={handleDownloadClick}
          >
            Download
          </Button>,
          // <Button key="back" onClick={handleCancel}>
          //   Luk
          // </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={() => {
              if (!preview.fileId) return alert('Noget gik galt');
              confirmDelete(preview.fileId);
            }}
          >
            Slet
          </Button>,
        ]}
        onCancel={handleCancel}
        // onOk is handled by the custom footer buttons
      >
        {previewImage && <ViewImage preview={preview} />}
      </Modal>
    </div>
  );
}

function ViewImage({ preview }: { preview: Preview }) {
  const { loading, fileUrl } = useGetDownloadUrl(preview?.previewImage as string);
  if (loading) return <div>Loading...</div>;
  if (!fileUrl) return <div>Noget gik galt...</div>;

  if (ContentType.PDF === preview.contentType) {
    return <embed src={fileUrl} type="application/pdf" width="100%" height="600px" />;
  }
  if (
    ContentType.XLSX === preview.contentType ||
    ContentType.XLS === preview.contentType ||
    ContentType.DOCX === preview.contentType ||
    ContentType.DOC === preview.contentType ||
    ContentType.PPT === preview.contentType ||
    ContentType.PPTX === preview.contentType
  ) {
    const icon = getContentTypeIconMap(preview.contentType, '', {
      height: 100,
      width: 100,
      borderRadius: 4,
      fontSize: 100,
    });

    return (
      <div className="flex bg-muted p-4 rounded-lg flex-col items-center gap-2">
        {icon}
        <p>Download for at se filen</p>
      </div>
    );
  }
  return <img alt="example" style={{ width: '100%' }} src={fileUrl} />;
}
function ViewFile({
  icon,
  filePath,
  openPreview,
}: {
  icon: any;
  filePath: string;
  openPreview: VoidFunction;
}) {
  const { fileUrl } = useGetDownloadUrl(filePath);

  if (!fileUrl) return null;
  return (
    <Hovered>
      {icon}
      <Button
        onClick={openPreview}
        type="link"
        style={{
          position: 'absolute',
          textAlign: 'center',
          bottom: 0,
          left: 0,
          right: 0,
          paddingLeft: '7px',
        }}
      >
        <EyeOutlined />
      </Button>
    </Hovered>
  );
}

const Hovered = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${StyleGuide.palette.grey300};
  position: relative;
  height: 100%;
  margin-right: 4px;
  transition: all 0.3s;
  cursor: pointer;
  .ant-btn {
    display: none;
  }
  &:hover {
    opacity: 0.8;
    .ant-btn {
      display: block;
    }
  }
`;
